export class IdleMonitor {
    allEvents = ['mousemove', 'mousewheel', 'scroll', 'mousedown', 'keypress', 'keyup', 'touchstart'];
    rapidEvents = ['mousemove', 'mousewheel', 'scroll'];

    constructor(name, duration = 3_600_000, warning_duration = 120_000, warning_interval = 1000, heartbeat_interval = 60_000) {
        this.name = name;
        this.status = 'active';
        this.latestActivity = Date.now();
        this.rebindEventsTimeout = null;
        this.warningTimeout = null;
        this.warningIntervalTimeout = null;
        this.heartbeatIntervalTimeout = null;
        this.heartbeatInterval = heartbeat_interval;
        this.latestReceivedHeartbeat = Date.now();
        this.initialized = false;
        this.warningDuration = warning_duration;
        if (this.warningDuration < 0) {
            this.warningDuration = 0;
        }
        this.warningInterval = warning_interval;
        if (this.warningInterval < 1000) {
            this.warningInterval = 1000;
        }
        this.duration = duration;
        if (this.duration < this.warningDuration) {
            this.duration = this.warningDuration;
        }
        if (this.duration < 1000) {
            this.duration = 1000;
        }
        this.setStoredActivity(this.latestActivity);
    }

    setStatus(status) {
        const oldStatus = this.status;
        this.status = status;
        if (this.status !== oldStatus) {
            // fire status changed event
            this.dispatchEvent('status.idle');
            if (this.status === 'active') {
                // fire reactivated event (ie warning => active)
                this.dispatchEvent('active.idle');
            }
        }
    }

    dispatchEvent(name, additional_detail = {}) {
        const elapsedIdleTime = Date.now() - this.latestActivity;
        const detail = Object.assign({
            name: this.name,
            activity: this.latestActivity,
            elapsed: elapsedIdleTime,
            remaining: Math.max(0, this.duration - elapsedIdleTime),
            status: this.status,
            latestHeartbeat: this.latestHeartbeat(),
        }, additional_detail);
        const ev = new CustomEvent(name, { detail });
        window.dispatchEvent(ev);
    }

    remaining(now = null) {
        if (now === null) {
            now = Date.now();
        }
        const elapsed = now - this.latestActivity;
        return Math.max(0, this.duration - elapsed);
    }

    fireWarningEvents() {
        if (!this.checkHeartbeat()) {
            return;
        }
        this.clearWarningTimeout();
        const elapsedIdleTime = Date.now() - this.latestActivity;
        if (elapsedIdleTime < this.duration) {
            this.setWarningInterval();
            this.setStatus('warning');
            // fire warning duration event
            this.dispatchEvent('warning.idle');
            return;
        }
        this.clearWarningInterval();
        this.setStatus('expired');
        // fire last duration expired event
        this.dispatchEvent('expired.idle');
    }

    latestHeartbeat() {
        const now = Date.now();
        const localElapsed = now - this.latestReceivedHeartbeat;
        if (localElapsed < 2 * this.heartbeatInterval) {
            return {
                'elapsed': localElapsed,
                'heartbeat': this.latestReceivedHeartbeat,
                'method': 'local',
            };
        }
        const storedHeartbeat = parseInt(localStorage.getItem(this.heartbeatStorageKey()), 10);
        const storedElapsed = now - storedHeartbeat;
        return {
            'elapsed': storedElapsed,
            'heartbeat': storedHeartbeat,
            'method': 'storage',
        };
    }
    isHeartBeating() {
        let heartbeat = this.latestHeartbeat();
        return heartbeat.elapsed < this.duration;
    }
    checkHeartbeat() {
        if (this.isHeartBeating()) {
            return true;
        }
        this.clearWarningTimeout();
        this.clearWarningInterval();
        this.setStatus('expired');
        this.dispatchEvent('expired.idle');
        return false;
    }
    heartbeat() {
        const now = Date.now();
        const threshold = this.heartbeatInterval / 2;
        const elapsed = now - this.latestReceivedHeartbeat;
        if (elapsed < threshold) {
            // another heartbeat fired in a different window recently
            return;
        }
        localStorage.setItem(this.heartbeatStorageKey(), '' + now);
        this.latestReceivedHeartbeat = now;
    }

    setWarningTimeout(replace = false) {
        if (this.warningTimeout) {
            if (!replace) {
                return;
            }
            this.clearWarningTimeout();
        }
        const firstWarningAt = this.duration - this.warningDuration;
        this.warningTimeout = setTimeout(() => this.fireWarningEvents(), firstWarningAt);
    }
    clearWarningTimeout() {
        clearTimeout(this.warningTimeout);
        this.warningTimeout = null;
    }

    setWarningInterval(replace = false) {
        if (this.warningIntervalTimeout) {
            if (!replace) {
                return;
            }
            this.clearWarningInterval();
        }
        this.warningIntervalTimeout = setInterval(() => this.fireWarningEvents(), this.warningInterval);
    }
    clearWarningInterval() {
        clearInterval(this.warningIntervalTimeout);
        this.warningIntervalTimeout = null;
    }

    setHeartbeatInterval(replace = false) {
        if (this.heartbeatIntervalTimeout) {
            if (!replace) {
                return;
            }
            this.clearHeartbeatInterval();
        }
        this.heartbeatIntervalTimeout = setInterval(() => {
            if (!this.checkHeartbeat()) {
                return;
            }
            this.heartbeat();
        }, this.heartbeatInterval);
    }
    clearHeartbeatInterval() {
        clearInterval(this.heartbeatIntervalTimeout);
        this.heartbeatIntervalTimeout = null;
    }

    storageKey() {
        return this.name + '.latestActivity';
    }
    heartbeatStorageKey() {
        return this.name + '.heartbeat';
    }

    getStoredActivity() {
        return parseInt(localStorage.getItem(this.storageKey()), 10);
    }
    setStoredActivity(activity) {
        localStorage.setItem(this.storageKey(), '' + activity);
    }

    init() {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        let monitor = this;

        function rebindRapidEvents() {
            monitor.rapidEvents.forEach(function (name) {
                document.addEventListener(name, onActivity, {
                    passive: true,
                    capture: true,
                });
            });
        }

        function suppressRapidEvents(timeout = 5_000) {
            monitor.rapidEvents.forEach(function (name) {
                document.removeEventListener(name, onActivity, {capture: true});
            });
            if (monitor.rebindEventsTimeout) {
                clearTimeout(monitor.rebindEventsTimeout);
                monitor.rebindEventsTimeout = null;
            }
            if (timeout !== null && timeout >= 0) {
                monitor.rebindEventsTimeout = setTimeout(rebindRapidEvents, timeout);
            }
        }

        function scheduleIdleTimeout() {
            monitor.clearWarningInterval();
            monitor.setWarningTimeout(true);
            monitor.setStatus('active');
        }

        function onActivity(ev) {
            if (!monitor.checkHeartbeat()) {
                return;
            }
            suppressRapidEvents();
            const newActivity = Date.now();
            const storedActivity = monitor.getStoredActivity();
            if (newActivity - storedActivity > 3000) {
                monitor.setStoredActivity(newActivity);
            }
            if (newActivity - monitor.latestActivity > 1000) {
                scheduleIdleTimeout();
            }
            monitor.latestActivity = newActivity;
        }

        function onRemoteActivity(ev) {
            if (ev.key !== monitor.storageKey()) {
                return;
            }
            monitor.latestActivity = ev.newValue;
            scheduleIdleTimeout();
        }
        function onRemoteHeartbeat(ev) {
            if (ev.key !== monitor.heartbeatStorageKey()) {
                return;
            }
            monitor.latestReceivedHeartbeat = ev.newValue;
        }

        window.addEventListener('storage', onRemoteActivity);
        window.addEventListener('load', onActivity, {
            passive: true,
            capture: true,
        });
        this.allEvents.forEach(function (name) {
            document.addEventListener(name, onActivity, {
                passive: true,
                capture: true,
            });
        });
        window.addEventListener('freeze', function (ev) {
            monitor.clearHeartbeatInterval();
            suppressRapidEvents(null);
        });
        window.addEventListener('resume', function (ev) {
            if (!monitor.checkHeartbeat()) {
                return;
            }
            monitor.setHeartbeatInterval();
            rebindRapidEvents();
        });

        this.heartbeat();
        this.setHeartbeatInterval();
        scheduleIdleTimeout();
    }
}

window.IdleMonitor = IdleMonitor;
